import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  MeetingLocation,
  MeetingSource,
  GuestGetBookedMeetingResponseV2,
  MeetingDetail,
} from '@vendasta/meetings';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { BookMeNowStoreService } from '../../book-me-now-store.service';
import { TimeSlot } from '../../bookme/bookme';
import {
  buildDisplayTitle,
  extractEventTypeName,
  formatDateForDisplay,
  formatDurationFromDates,
  formatNames,
  getMeetingSourceText,
  isBatchBooking,
} from '../../util';

const HELP_LINK_BY_MEETING_SOURCE: Partial<Record<MeetingSource, string>> = {
  [MeetingSource.MEETING_SOURCE_GOOGLE_MEET]:
    'https://workspace.google.com/products/meet/',
  [MeetingSource.MEETING_SOURCE_MICROSOFT]:
    'https://support.microsoft.com/en-us/office/join-a-meeting-in-microsoft-teams-1613bb53-f3fa-431e-85a9-d6a91e3468c9',
  [MeetingSource.MEETING_SOURCE_ZOOM]:
    'https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0060732',
};

interface TimeSlotFields {
  oldTimeSlot: TimeSlot;
  newTimeSlot: TimeSlot;
  oldDateFormatted: string;
  newDateFormatted: string;
  newDurationFormatted: string;
  oldTimezoneZone: string;
  newTimezoneZone: string;
  showOldTime: boolean;
}

interface RescheduleSuccessVm extends TimeSlotFields {
  title: string;
  hostNames: string;
  location: string;
  isVideo: boolean;
  isInPerson: boolean;
  showLocation: boolean;
  meetingSource: MeetingSource;
  meetingSourceText: string;
  meetingSourceLink: string;
  googleCalendarLink: string;
  outlookCalendarLink: string;
  rescheduleLink: string;
  cancelLink: string;
  businessLogo: string;
  hasMeetingLink: boolean;
  hasCalendarLinks: boolean;
  isBatch: boolean;
  displayTitle: string;
  meetings: MeetingDetailVm[];
}

interface MeetingDetailVm {
  meetingId: string;
  title: string;
  eventTypeName: string;
  hostNames: string;
  location: string;
  isVideo: boolean;
  isInPerson: boolean;
  showLocation: boolean;
  meetingSource: MeetingSource;
  meetingSourceText: string;
  meetingSourceLink: string;
  googleCalendarLink: string;
  outlookCalendarLink: string;
  hasMeetingLink: boolean;
  hasCalendarLinks: boolean;
}

@Component({
  selector: 'app-reschedule-success',
  templateUrl: './reschedule-success.component.html',
  styleUrls: ['./reschedule-success.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RescheduleSuccessComponent implements OnInit {
  timezoneOffset$: Observable<string>;
  vm$: Observable<RescheduleSuccessVm | null>;

  constructor(
    private readonly bookmenowService: BookMeNowStoreService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.timezoneOffset$ = this.bookmenowService.timezoneOffset$;

    const meetingId = this.route.snapshot.params.meetingId;

    this.vm$ = this.bookmenowService
      .getGuestBookedMeetingV2({ id: meetingId })
      .pipe(
        map(
          (
            meetingInfo: GuestGetBookedMeetingResponseV2 | null
          ): RescheduleSuccessVm | null => {
            const { isCancelled } = (meetingInfo ?? {}) as Pick<
              GuestGetBookedMeetingResponseV2,
              'isCancelled'
            >;

            if (typeof isCancelled === 'undefined') {
              return meetingInfo
                ? this.buildViewModel(meetingInfo)
                : this.buildViewModelFromState();
            }

            if (isCancelled) {
              this.router.navigate(['/cancel-info', meetingId, 'cancelled'], {
                queryParamsHandling: 'preserve',
              });
              return null;
            }

            return this.buildViewModel(meetingInfo);
          }
        ),
        catchError((err) => {
          console.error('Failed to load meeting info:', err);
          return of(this.buildViewModelFromState());
        }),
        shareReplay(1)
      );
  }

  private buildTimeSlotFields(
    meetingInfo?: GuestGetBookedMeetingResponseV2
  ): TimeSlotFields {
    const newTimeSlot = this.bookmenowService.getSelectedTimeSlot();
    const timezoneZone = meetingInfo?.timeZone?.id || '';

    // Use previousStartTime/previousEndTime from API for old time
    const oldTimeSlot: TimeSlot = meetingInfo?.previousStartTime
      ? {
          start: meetingInfo.previousStartTime,
          end: meetingInfo.previousEndTime,
          timezone: { region: '', zone: timezoneZone },
        }
      : this.bookmenowService.getOriginalMeetingTimeSlot();

    const showOldTime = Boolean(oldTimeSlot);

    // If page reloaded (no store data), use API response for current time
    const currentTimeSlot =
      newTimeSlot ||
      (meetingInfo
        ? {
            start: meetingInfo.startTime,
            end: meetingInfo.endTime,
            timezone: { zone: timezoneZone },
          }
        : null);

    return {
      oldTimeSlot,
      newTimeSlot: currentTimeSlot as TimeSlot,
      oldDateFormatted: formatDateForDisplay(oldTimeSlot?.start),
      newDateFormatted: formatDateForDisplay(currentTimeSlot?.start),
      newDurationFormatted: formatDurationFromDates(
        currentTimeSlot?.start,
        currentTimeSlot?.end
      ),
      oldTimezoneZone: oldTimeSlot?.timezone?.zone || '',
      newTimezoneZone: currentTimeSlot?.timezone?.zone || '',
      showOldTime,
    };
  }

  private buildViewModel(
    meetingInfo: GuestGetBookedMeetingResponseV2
  ): RescheduleSuccessVm {
    const isBatch = isBatchBooking(meetingInfo);
    const meetings: MeetingDetail[] = meetingInfo.meetings || [];
    const displayTitle = buildDisplayTitle(meetingInfo.title || '', isBatch, meetings);
    const meetingVms: MeetingDetailVm[] = isBatch ? meetings.map((m) => this.buildMeetingDetailVm(m)) : [];

    let hasMeetingLink = !!meetingInfo.meetingSourceLink;
    let hasCalendarLinks = !!meetingInfo.googleCalendarLink || !!meetingInfo.outlookCalendarLink;
    if (isBatch) {
      hasMeetingLink = hasMeetingLink || meetingVms.some((m) => m.hasMeetingLink);
      hasCalendarLinks = hasCalendarLinks || meetingVms.some((m) => m.hasCalendarLinks);
    }

    return {
      ...this.buildTimeSlotFields(meetingInfo),
      title: meetingInfo.title || '',
      hostNames: formatNames(meetingInfo.hostUserNames || []),
      location: meetingInfo.location || '',
      isVideo: isBatch
        ? meetingVms.some((m) => m.isVideo)
        : meetingInfo.meetingLocationType === MeetingLocation.VIDEO_MEETING ||
          meetingInfo.meetingLocationType === MeetingLocation.HYBRID_MEETING ||
          meetingInfo.meetingLocationType == null,
      isInPerson: isBatch
        ? meetingVms.some((m) => m.isInPerson)
        : meetingInfo.meetingLocationType === MeetingLocation.IN_PERSON_MEETING ||
          meetingInfo.meetingLocationType === MeetingLocation.HYBRID_MEETING,
      showLocation: isBatch
        ? meetingVms.some((m) => m.showLocation)
        : meetingInfo.meetingLocationType === MeetingLocation.IN_PERSON_MEETING ||
          meetingInfo.meetingLocationType === MeetingLocation.HYBRID_MEETING,
      meetingSource: meetingInfo.meetingSource,
      meetingSourceText: getMeetingSourceText(meetingInfo.meetingSource),
      meetingSourceLink: meetingInfo.meetingSourceLink || '',
      googleCalendarLink: meetingInfo.googleCalendarLink || '',
      outlookCalendarLink: meetingInfo.outlookCalendarLink || '',
      rescheduleLink: meetingInfo.rescheduleLink || '',
      cancelLink: meetingInfo.cancelLink || '',
      businessLogo: meetingInfo.businessLogo || '',
      hasMeetingLink,
      hasCalendarLinks,
      isBatch,
      displayTitle,
      meetings: meetingVms,
    };
  }

  private buildMeetingDetailVm(m: MeetingDetail): MeetingDetailVm {
    const isVideo =
      m.meetingLocationType === MeetingLocation.VIDEO_MEETING ||
      m.meetingLocationType === MeetingLocation.HYBRID_MEETING ||
      m.meetingLocationType == null;
    const isInPerson =
      m.meetingLocationType === MeetingLocation.IN_PERSON_MEETING ||
      m.meetingLocationType === MeetingLocation.HYBRID_MEETING;
    const title = m.title || '';
    return {
      meetingId: m.meetingId || '',
      title,
      eventTypeName: extractEventTypeName(title),
      hostNames: formatNames(m.hostUserNames || []),
      location: m.location || '',
      isVideo,
      isInPerson,
      showLocation: isInPerson || m.meetingLocationType === MeetingLocation.HYBRID_MEETING,
      meetingSource: m.meetingSource,
      meetingSourceText: getMeetingSourceText(m.meetingSource),
      meetingSourceLink: m.meetingSourceLink || '',
      googleCalendarLink: m.googleCalendarLink || '',
      outlookCalendarLink: m.outlookCalendarLink || '',
      hasMeetingLink: !!m.meetingSourceLink,
      hasCalendarLinks: !!m.googleCalendarLink || !!m.outlookCalendarLink,
    };
  }

  private buildViewModelFromState(): RescheduleSuccessVm | null {
    const state = this.bookmenowService.getCurrentState();
    if (!state.selectedTimeSlot) {
      return null;
    }

    return {
      ...this.buildTimeSlotFields(),
      title: state.displayName || state.calendar?.displayName || '',
      hostNames: state.displayName || '',
      location: state.meetingLocation || '',
      isVideo: true,
      isInPerson: false,
      showLocation: false,
      meetingSource: MeetingSource.MEETING_SOURCE_INVALID,
      meetingSourceText: 'Google',
      meetingSourceLink: '',
      googleCalendarLink: '',
      outlookCalendarLink: '',
      rescheduleLink: '',
      cancelLink: '',
      businessLogo: state.calendar?.displayLogoUrl || '',
      hasMeetingLink: false,
      hasCalendarLinks: false,
      isBatch: false,
      displayTitle: state.displayName || state.calendar?.displayName || '',
      meetings: [],
    };
  }

  openLinkInNewTab(url: string): void {
    if (url) {
      window.open(url, '_blank').focus();
    }
  }

  navigateToGoogleMaps(location: string): void {
    if (location) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`, '_blank');
    }
  }

  navigateToHelpLink(source: MeetingSource): void {
    const url = HELP_LINK_BY_MEETING_SOURCE[source];
    if (url) {
      this.openLinkInNewTab(url);
    }
  }
}
